/**
 * 2FA management modal — enrol, view backup codes, disable, regenerate codes.
 *
 * Replaces the standalone /setup-2fa page. Living in the settings modal
 * keeps the flow contextual: the user starts and finishes inside the same
 * Account screen, so reading their email + 2FA state in the row behind the
 * modal is just a backdrop click away. Sub-flows are routed by an internal
 * `view` state, not by URL — no deep-link is exposed.
 *
 * Available flows:
 *   - enrol-confirm-password → enrol-show-qr → enrol-success
 *   - manage (when already enabled)
 *       ↳ disable-confirm-password → manage (closed)
 *       ↳ regenerate-confirm-password → regenerate-show-codes
 *
 * BetterAuth surface used:
 *   authClient.twoFactor.enable({ password })  → { totpURI, backupCodes }
 *   authClient.twoFactor.verifyTotp({ code })  → flips twoFactorEnabled=true
 *   authClient.twoFactor.disable({ password })
 *   authClient.twoFactor.generateBackupCodes({ password }) → { backupCodes }
 */
import { useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { ShieldCheck, KeyRound, ShieldOff, Copy, Check } from 'lucide-react';
import { authClient } from '@/shared/auth/authClient';
import { Button, Dialog, Form, FormTextField, Badge } from '@/interfaces/learner/ui';
import LoadingIcon from '@/foundation/ui/LoadingIcon';
import { PinCodeInput } from './PinCodeInput';

// ─── Schemas ────────────────────────────────────────────────────────────────

const passwordSchema = z.object({ password: z.string().min(1, 'Password is required') });
type PasswordValues = z.infer<typeof passwordSchema>;

// ─── View states ────────────────────────────────────────────────────────────

type View =
  // Disabled-state entry views.
  | { kind: 'enrol-password' }
  | { kind: 'enrol-qr'; totpURI: string; backupCodes: string[] }
  | { kind: 'enrol-success'; backupCodes: string[] }
  // Enabled-state entry view (the menu of management actions).
  | { kind: 'manage' }
  // Disable flow.
  | { kind: 'disable-password' }
  // Regenerate-codes flow.
  | { kind: 'regenerate-password' }
  | { kind: 'regenerate-show'; backupCodes: string[] };

interface Setup2FAModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Whether 2FA is currently enabled — drives initial view. */
  isEnabled: boolean;
  /** Optional callback after a successful enable/disable so the parent can refresh state. */
  onStateChange?: () => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function Setup2FAModal({ isOpen, onClose, isEnabled, onStateChange }: Setup2FAModalProps) {
  // Initial view depends on enable state at open time. We re-derive it via
  // a key on the inner Body so a fresh open always starts from the right
  // place even after re-opens within the same session.
  const initialView: View = isEnabled ? { kind: 'manage' } : { kind: 'enrol-password' };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Two-factor authentication" size="md">
      {/* The `{isOpen && ...}` gate already unmounts the body on close, so
          internal state resets cleanly between opens. We deliberately do
          NOT key on `isEnabled` — during the enrolment success step the
          parent's `isEnabled` flips false→true (we just enabled 2FA) and
          a key change there would remount this body, snapping it back to
          the 'manage' view and wiping out the backup codes the user has
          not yet read. The captured `initialView` from open time is the
          correct anchor for the whole flow. */}
      {isOpen && (
        <ModalBody initialView={initialView} onClose={onClose} onStateChange={onStateChange} />
      )}
    </Dialog>
  );
}

// ─── Inner body (manages the view machine) ──────────────────────────────────

function ModalBody({
  initialView,
  onClose,
  onStateChange,
}: {
  initialView: View;
  onClose: () => void;
  onStateChange?: () => void;
}) {
  const [view, setView] = useState<View>(initialView);

  // ── Enrolment ─────────────────────────────────────────────────────────────

  if (view.kind === 'enrol-password') {
    return (
      <PasswordPrompt
        title="Confirm your password to enable 2FA"
        submitLabel="Continue"
        onCancel={onClose}
        onSubmit={async ({ password }) => {
          const { data, error } = await authClient.twoFactor.enable({ password });
          if (error || !data) {
            toast.error(error?.message ?? 'Could not start enrolment');
            return false;
          }
          setView({ kind: 'enrol-qr', totpURI: data.totpURI, backupCodes: data.backupCodes });
          return true;
        }}
      />
    );
  }

  if (view.kind === 'enrol-qr') {
    return (
      <EnrolQrStep
        totpURI={view.totpURI}
        onCancel={onClose}
        onVerified={() => {
          setView({ kind: 'enrol-success', backupCodes: view.backupCodes });
          onStateChange?.();
        }}
      />
    );
  }

  if (view.kind === 'enrol-success') {
    return (
      <BackupCodesView
        title="2FA is enabled"
        intro="Save these one-time backup codes. Each one can be used in place of a 6-digit code if you lose your authenticator app."
        backupCodes={view.backupCodes}
        primaryLabel="Done"
        onPrimary={onClose}
        leadingIcon={<ShieldCheck size={18} />}
      />
    );
  }

  // ── Management menu ───────────────────────────────────────────────────────

  if (view.kind === 'manage') {
    return (
      <div className="space-y-3 pt-1">
        <p className="text-sm text-[var(--color-text-secondary)]">
          Two-factor authentication is currently active on your account.
        </p>
        <ActionRow
          icon={<KeyRound size={16} />}
          label="Regenerate backup codes"
          description="Issue a new set of one-time codes. The old set stops working immediately."
          onPress={() => setView({ kind: 'regenerate-password' })}
        />
        <ActionRow
          icon={<ShieldOff size={16} />}
          label="Disable 2FA"
          description="Remove the second-factor requirement from your account."
          danger
          onPress={() => setView({ kind: 'disable-password' })}
        />
        <div className="flex justify-end pt-2">
          <Button variant="ghost" onPress={onClose}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  // ── Disable ──────────────────────────────────────────────────────────────
  //
  // Password gate. We don't ask for a TOTP/backup code because BetterAuth's
  // /two-factor/disable endpoint only checks the password — adding a UI
  // gate on top would be theatre that any direct API caller could skip.
  // After a successful disable we revoke all other sessions so any
  // in-flight attacker session is killed.

  if (view.kind === 'disable-password') {
    return (
      <PasswordPrompt
        title="Enter your current password to disable two-factor authentication."
        submitLabel="Disable 2FA"
        submitVariant="danger"
        onCancel={() => setView({ kind: 'manage' })}
        onSubmit={async ({ password }) => {
          const { error } = await authClient.twoFactor.disable({ password });
          if (error) {
            toast.error(error.message ?? 'Could not disable 2FA');
            return false;
          }
          // Belt-and-braces: kill every other live session so an attacker
          // who triggered this is logged out everywhere they were.
          await authClient.revokeOtherSessions().catch(() => {
            /* non-fatal — disable already succeeded */
          });
          toast.success('2FA disabled — other sessions signed out.');
          onStateChange?.();
          onClose();
          return true;
        }}
      />
    );
  }

  // ── Regenerate codes ─────────────────────────────────────────────────────
  //
  // Same password-only gate as disable — BetterAuth's
  // /two-factor/generate-backup-codes only checks password. We revoke
  // other sessions afterwards so a stale attacker session can't keep
  // using the old (still-known-to-them) codes after they're rotated.

  if (view.kind === 'regenerate-password') {
    return (
      <PasswordPrompt
        title="Enter your current password. Your existing recovery codes will stop working as soon as the new set is issued."
        submitLabel="Regenerate codes"
        onCancel={() => setView({ kind: 'manage' })}
        onSubmit={async ({ password }) => {
          const { data, error } = await authClient.twoFactor.generateBackupCodes({ password });
          if (error || !data) {
            toast.error(error?.message ?? 'Could not regenerate codes');
            return false;
          }
          await authClient.revokeOtherSessions().catch(() => {
            /* non-fatal */
          });
          setView({ kind: 'regenerate-show', backupCodes: data.backupCodes });
          return true;
        }}
      />
    );
  }

  if (view.kind === 'regenerate-show') {
    return (
      <BackupCodesView
        title="New backup codes"
        intro="Save these somewhere safe — your previous codes no longer work."
        backupCodes={view.backupCodes}
        primaryLabel="Done"
        onPrimary={onClose}
      />
    );
  }

  // Exhaustive guard.
  const _never: never = view;
  return _never;
}

// ─── Reusable steps ─────────────────────────────────────────────────────────

function PasswordPrompt({
  title,
  submitLabel,
  submitVariant = 'accent',
  onCancel,
  onSubmit,
}: {
  title: string;
  submitLabel: string;
  submitVariant?: 'accent' | 'danger' | 'primary';
  onCancel: () => void;
  /** Returns true if step succeeded (parent advanced view). */
  onSubmit: (values: PasswordValues) => Promise<boolean>;
}) {
  const form = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: '' },
    mode: 'onBlur',
  });

  const submitting = form.formState.isSubmitting;

  const handle: SubmitHandler<PasswordValues> = async (values) => {
    await onSubmit(values);
  };

  return (
    <Form onSubmit={form.handleSubmit(handle)} className="space-y-4 pt-1">
      <p className="text-sm">{title}</p>
      <FormTextField
        control={form.control}
        name="password"
        label="Current password"
        type="password"
        autoComplete="current-password"
        isRequired
        autoFocus
      />
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" onPress={onCancel} isDisabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" variant={submitVariant} isDisabled={submitting}>
          {submitting ? (
            <span className="flex items-center gap-2">
              <LoadingIcon size="sm" />
              Working...
            </span>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </Form>
  );
}

function EnrolQrStep({
  totpURI,
  onCancel,
  onVerified,
}: {
  totpURI: string;
  onCancel: () => void;
  onVerified: () => void;
}) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // showError stays false until the user clicks Verify with an invalid code,
  // so they don't see a red error box just from typing partial digits.
  const [showError, setShowError] = useState(false);

  const lengthValid = /^\d{6}$/.test(code);

  async function verify() {
    if (!lengthValid) {
      setShowError(true);
      return;
    }
    setSubmitting(true);
    const { error } = await authClient.twoFactor.verifyTotp({ code });
    setSubmitting(false);
    if (error) {
      toast.error(error.message ?? 'Invalid code');
      setShowError(true);
      setCode('');
      return;
    }
    toast.success('2FA enabled');
    onVerified();
  }

  return (
    <div className="space-y-4 pt-1">
      <p className="text-sm">
        Scan this code with your authenticator app (1Password, Authy, Google Authenticator), then
        enter the 6-digit code it displays.
      </p>
      <div
        className="flex justify-center bg-white p-4 rounded-xl border"
        style={{ borderColor: 'var(--color-surface-border)' }}
      >
        <QRCodeSVG value={totpURI} size={184} />
      </div>
      <PinCodeInput
        label="6-digit code"
        value={code}
        onChange={(v) => {
          setCode(v);
          // Hide the error as soon as the user starts editing again.
          if (showError) setShowError(false);
        }}
        onComplete={() => {
          // Don't auto-submit if the user is mid-paste with an invalid code;
          // the explicit button keeps control predictable.
        }}
        autoFocus
        isInvalid={showError && !lengthValid}
        errorMessage={
          showError && !lengthValid ? 'Enter all 6 digits from your authenticator app.' : undefined
        }
      />
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" onPress={onCancel} isDisabled={submitting}>
          Cancel
        </Button>
        <Button variant="accent" onPress={verify} isDisabled={submitting}>
          {submitting ? (
            <span className="flex items-center gap-2">
              <LoadingIcon size="sm" />
              Verifying...
            </span>
          ) : (
            'Enable 2FA'
          )}
        </Button>
      </div>
    </div>
  );
}

function BackupCodesView({
  title,
  intro,
  backupCodes,
  primaryLabel,
  onPrimary,
  leadingIcon,
}: {
  title: string;
  intro: string;
  backupCodes: string[];
  primaryLabel: string;
  onPrimary: () => void;
  leadingIcon?: React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);

  async function copyAll() {
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  }

  return (
    <div className="space-y-4 pt-1">
      <div className="flex items-center gap-2">
        {leadingIcon && <span style={{ color: 'var(--color-success-text)' }}>{leadingIcon}</span>}
        <h3 className="text-base font-semibold">{title}</h3>
        <Badge variant="success">Active</Badge>
      </div>
      <p className="text-sm text-[var(--color-text-secondary)]">{intro}</p>
      <ul
        className="font-mono text-sm grid grid-cols-2 gap-x-4 gap-y-1 p-3 rounded-lg"
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
        }}
      >
        {backupCodes.map((c) => (
          <li key={c} style={{ letterSpacing: '0.04em' }}>
            {c}
          </li>
        ))}
      </ul>
      <div className="flex gap-2 justify-between items-center">
        <Button variant="ghost" size="sm" onPress={copyAll}>
          <span className="flex items-center gap-2">
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy codes'}
          </span>
        </Button>
        <Button variant="accent" onPress={onPrimary}>
          {primaryLabel}
        </Button>
      </div>
    </div>
  );
}

function ActionRow({
  icon,
  label,
  description,
  onPress,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  onPress: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      className="w-full flex items-center gap-3 text-left rounded-xl px-3 py-3 transition"
      style={{
        background: 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        cursor: 'pointer',
      }}
    >
      <span
        className="flex items-center justify-center h-8 w-8 rounded-lg"
        style={{
          background: 'var(--color-surface-solid)',
          color: danger ? 'var(--color-error-text)' : 'var(--color-text-muted)',
        }}
      >
        {icon}
      </span>
      <span className="flex-1">
        <span
          className="block text-sm font-semibold"
          style={{ color: danger ? 'var(--color-error-text)' : 'var(--color-text)' }}
        >
          {label}
        </span>
        <span className="block text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          {description}
        </span>
      </span>
    </button>
  );
}
